import React, { useEffect, useRef, useState } from "react";
import "./RichTextToolbarControls.css";

export const TEXT_COLOURS = [
  "#000000",
  "#334155",
  "#b91c1c",
  "#c2410c",
  "#a16207",
  "#15803d",
  "#0f6f72",
  "#1d4ed8",
  "#6d28d9",
];

export const HIGHLIGHT_COLOURS = [
  "#ffffff",
  "#fef08a",
  "#fed7aa",
  "#fecaca",
  "#bbf7d0",
  "#bae6fd",
  "#ddd6fe",
  "#e5e7eb",
];

export default function RichTextToolbarControls({
  activeFormats = {},
  disabled = false,
  showLists = false,
  onToggle,
  onToggleList,
  onApplyColour,
}) {
  const controlsRef = useRef(null);
  const [openPicker, setOpenPicker] = useState(null);
  const preventEditorBlur = (event) => event.preventDefault();

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (!controlsRef.current?.contains(event.target)) setOpenPicker(null);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
  }, []);

  const applyColour = (format, colour) => {
    onApplyColour?.(format, colour);
    setOpenPicker(null);
  };

  return (
    <div className="rich-text-format-controls" ref={controlsRef}>
      {[
        ["bold", "B", "Bold"],
        ["italic", "I", "Italic"],
        ["underline", "U", "Underline"],
      ].map(([format, label, title]) => (
        <button
          key={format}
          type="button"
          className={`rich-text-icon-button rich-text-${format} ${activeFormats[format] ? "is-active" : ""}`}
          aria-label={title}
          aria-pressed={Boolean(activeFormats[format])}
          title={title}
          disabled={disabled}
          data-no-standard-button="true"
          onMouseDown={preventEditorBlur}
          onClick={() => onToggle?.(format)}
        >
          {label}
        </button>
      ))}

      {showLists ? (
        <>
          <button
            type="button"
            className={`rich-text-icon-button ${activeFormats.orderedList ? "is-active" : ""}`}
            aria-label="Numbered list"
            aria-pressed={Boolean(activeFormats.orderedList)}
            title="Numbered list"
            disabled={disabled}
            data-no-standard-button="true"
            onMouseDown={preventEditorBlur}
            onClick={() => onToggleList?.("ordered")}
          >
            <i className="fa-solid fa-list-ol" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`rich-text-icon-button ${activeFormats.bulletList ? "is-active" : ""}`}
            aria-label="Bullet list"
            aria-pressed={Boolean(activeFormats.bulletList)}
            title="Bullet list"
            disabled={disabled}
            data-no-standard-button="true"
            onMouseDown={preventEditorBlur}
            onClick={() => onToggleList?.("bullet")}
          >
            <i className="fa-solid fa-list-ul" aria-hidden="true" />
          </button>
        </>
      ) : null}

      <div className="rich-text-colour-control">
        <button
          type="button"
          className="rich-text-icon-button rich-text-font-colour"
          aria-label="Font colour"
          aria-expanded={openPicker === "color"}
          title="Font colour"
          disabled={disabled}
          data-no-standard-button="true"
          onMouseDown={preventEditorBlur}
          onClick={() =>
            setOpenPicker((current) => (current === "color" ? null : "color"))
          }
        >
          <span className="rich-text-colour-glyph">A</span>
        </button>
        {openPicker === "color" ? (
          <div className="rich-text-colour-palette" role="menu" aria-label="Font colours">
            {TEXT_COLOURS.map((colour) => (
              <button
                key={colour}
                type="button"
                className="rich-text-colour-swatch"
                style={{ backgroundColor: colour }}
                aria-label={`Use font colour ${colour}`}
                title={colour}
                data-no-standard-button="true"
                onMouseDown={preventEditorBlur}
                onClick={() => applyColour("color", colour)}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="rich-text-colour-control">
        <button
          type="button"
          className="rich-text-icon-button rich-text-highlight"
          aria-label="Highlight colour"
          aria-expanded={openPicker === "background"}
          title="Highlight colour"
          disabled={disabled}
          data-no-standard-button="true"
          onMouseDown={preventEditorBlur}
          onClick={() =>
            setOpenPicker((current) =>
              current === "background" ? null : "background",
            )
          }
        >
          <span className="rich-text-colour-glyph">A</span>
        </button>
        {openPicker === "background" ? (
          <div
            className="rich-text-colour-palette"
            role="menu"
            aria-label="Highlight colours"
          >
            {HIGHLIGHT_COLOURS.map((colour) => (
              <button
                key={colour}
                type="button"
                className="rich-text-colour-swatch"
                style={{ backgroundColor: colour }}
                aria-label={`Use highlight colour ${colour}`}
                title={colour}
                data-no-standard-button="true"
                onMouseDown={preventEditorBlur}
                onClick={() => applyColour("background", colour)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
